import React from 'react';

import CardDialog from '~/components/CardDialog';
import { ClaimContextProvider } from '~/components/ClaimContainer';
import SuggestedEmailNotificationDialog from '~/components/ClaimNotificationsCard/SuggestedEmailNotificationDialog';
import type {
  CommunicationSuggestionClaimNotificationModel,
  UpdateNotification,
} from '~/components/ClaimNotificationsCard/types';
import MinimizedEmailDialogLauncher from '~/components/communications/EmailCommunicationCard/MinimizedEmailDialogLauncher';
import SendSmsCommunicationCardContainer from '~/components/communications/SmsCommunicationCard/SmsCommunicationCard';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { MIXPANEL_EVENT_SOURCES } from '~/pocs/mixpanel';
import { reportErrorInProductionOrThrow } from '~/Utils';

interface Props {
  id: string;
  onClose: () => void;
  claimNotification: CommunicationSuggestionClaimNotificationModel;
  onUpdate: () => void;
  onMarkAsUnread?: (notificationId: number) => void;
  onUpdateNotification?: UpdateNotification;
}
const ClaimNotificationCommunicationSuggestion: React.FC<Props> = ({
  onClose,
  claimNotification,
  onUpdate,
  onMarkAsUnread,
  onUpdateNotification,
}) => {
  const isEmailSuggestion = claimNotification.subtype === 'email';
  // The email editor is handed to the minimized-dialog registry below, and fetches the claim itself
  // once it is instantiated there; asking for it here as well would only duplicate the request.
  const [claim, isLoadingClaim, isError, reloadClaim] = useFetchClaim(
    isEmailSuggestion ? undefined : claimNotification.claim_id
  );
  const { user } = useCms();

  if (isEmailSuggestion) {
    return (
      <MinimizedEmailDialogLauncher
        barHeader={`Suggested Email: ${claimNotification.subject ?? ''}`}
        mixpanelSource={MIXPANEL_EVENT_SOURCES.NOTIFICATION_SUGGESTED_EMAIL}
        dialogComponent={SuggestedEmailNotificationDialog}
        dialogProps={{
          claimNotification,
          onUpdate,
          onMarkAsUnread,
          onUpdateNotification,
        }}
        // The notification card has handed the editor over to the registry; it no longer has one to
        // render itself.
        onLaunched={onClose}
      />
    );
  }

  if (isLoadingClaim) {
    return null;
  }

  if (!claim || isError) {
    throw Error('No claim for claim notification suggestion');
  }
  const contact = claim?.contacts?.find((c) => c.id === claimNotification.to_contact_id);
  const populatedContact = contact ? contactPopulate(contact) : undefined;

  if (claimNotification?.subtype !== 'sms') {
    reportErrorInProductionOrThrow(
      `Claim notification communication channel ${claimNotification.subtype} is not supported`
    );
  }

  return (
    <ClaimContextProvider claim={claim} refreshData={async () => await reloadClaim()}>
      {/* This can heppen when the user is not allowed to send SMS but has access to the suggestion notification*/}
      {claimNotification.subtype === 'sms' && !user.is_sending_sms_allowed && (
        <CardDialog open isDialog title="Sending SMS capability required" onClose={onClose} maxWidth="sm" fullWidth>
          <div className="p-4">
            <Text variant={Text.VARIANTS.SM} className="text-red-600">
              You do not have capability to send SMS messages.
              <br />
              This is a Suggestion Notification to send an SMS upon automatic communication rule To{' '}
              {claimNotification.recipient_label}:
              <br />
              Message:
              <br />
              {claimNotification.body}
            </Text>
          </div>
        </CardDialog>
      )}
      {claimNotification.subtype === 'sms' && user.is_sending_sms_allowed && (
        <SendSmsCommunicationCardContainer
          contact={populatedContact}
          contactPhoneId={populatedContact?.primary_phone?.id}
          onCancel={onClose}
          messageContent={claimNotification.body}
          onSendSms={() => {
            onUpdate();
            onClose();
          }}
          communicationSuggestionNotificationId={claimNotification.id}
        />
      )}
    </ClaimContextProvider>
  );
};

export default ClaimNotificationCommunicationSuggestion;
